import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Calendar,
  Monitor,
  Smartphone,
  Globe,
  User,
  Play,
  X,
  ChevronLeft,
  ChevronRight,
  Send,
  Loader2,
  AlertCircle,
  MessageSquare,
  ExternalLink,
  LogOut,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Separator } from "../components/ui/separator";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance with auth header
const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
});

const BugDetailPage = () => {
  const { recordId } = useParams();
  const navigate = useNavigate();
  const [bug, setBug] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [commentForm, setCommentForm] = useState({ comment: "" });
  const [submittingComment, setSubmittingComment] = useState(false);
  const username = localStorage.getItem("username");

  useEffect(() => {
    fetchBugDetails();
    fetchComments();
  }, [recordId]);

  const fetchBugDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/bugs/${recordId}`, getAuthHeaders());
      setBug(response.data);
      setError(null);
    } catch (err) {
      console.error("Error fetching bug details:", err);
      if (err.response?.status === 401) {
        handleLogout();
        return;
      }
      setError("Failed to load bug details. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async () => {
    try {
      const response = await axios.get(`${API}/bugs/${recordId}/comments`, getAuthHeaders());
      setComments(response.data);
    } catch (err) {
      console.error("Error fetching comments:", err);
      if (err.response?.status === 401) {
        handleLogout();
      }
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("username");
    toast.info("Session expired. Please login again.");
    navigate("/login");
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!commentForm.comment.trim()) {
      toast.error("Please enter a comment");
      return;
    }

    try {
      setSubmittingComment(true);
      await axios.post(`${API}/bugs/${recordId}/comments`, {
        author: username,
        comment: commentForm.comment,
      }, getAuthHeaders());
      toast.success("Comment added successfully!");
      setCommentForm({ comment: "" });
      // Small delay to ensure Airtable has processed the new record
      await new Promise(resolve => setTimeout(resolve, 500));
      await fetchComments();
    } catch (err) {
      console.error("Error submitting comment:", err);
      if (err.response?.status === 401) {
        handleLogout();
        return;
      }
      toast.error("Failed to add comment. Please try again.");
    } finally {
      setSubmittingComment(false);
    }
  };

  const getSeverityBadgeClass = (severity) => {
    const classes = {
      Critical: "bg-red-200 text-red-900 border-red-300",
      High: "bg-red-100 text-red-800 border-red-200",
      Medium: "bg-yellow-100 text-yellow-800 border-yellow-200",
      Low: "bg-blue-100 text-blue-800 border-blue-200",
      Enhancement: "bg-purple-100 text-purple-800 border-purple-200",
      "UI/UX Suggestion": "bg-pink-100 text-pink-800 border-pink-200",
    };
    return classes[severity] || "bg-gray-100 text-gray-800 border-gray-200";
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      Open: "bg-green-100 text-green-800 border-green-200",
      "In Progress": "bg-blue-100 text-blue-800 border-blue-200",
      Closed: "bg-gray-100 text-gray-800 border-gray-200",
      "Not a Bug": "bg-orange-100 text-orange-800 border-orange-200",
    };
    return classes[status] || "bg-gray-100 text-gray-800 border-gray-200";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getPlayableVideoUrl = (url) => {
    // Return null for empty, whitespace-only, or undefined URLs
    if (!url || !url.trim()) return null;
    
    const trimmedUrl = url.trim();

    // YouTube
    if (trimmedUrl.includes("youtube.com") || trimmedUrl.includes("youtu.be")) {
      const videoId = trimmedUrl.includes("youtu.be")
        ? trimmedUrl.split("/").pop()
        : new URL(trimmedUrl).searchParams.get("v");
      if (!videoId) return null;
      return { type: "youtube", url: `https://www.youtube.com/embed/${videoId}` };
    }

    // Vimeo
    if (trimmedUrl.includes("vimeo.com")) {
      const videoId = trimmedUrl.split("/").pop();
      if (!videoId) return null;
      return { type: "vimeo", url: `https://player.vimeo.com/video/${videoId}` };
    }

    // Dropbox - convert to direct link
    if (trimmedUrl.includes("dropbox.com")) {
      const directUrl = trimmedUrl
        .replace("www.dropbox.com", "dl.dropboxusercontent.com")
        .replace("?dl=0", "")
        .replace("?dl=1", "");
      return { type: "direct", url: directUrl };
    }

    // Direct MP4 or other video
    if (trimmedUrl.match(/\.(mp4|webm|ogg|mov)$/i) || trimmedUrl.includes("dl.dropboxusercontent.com")) {
      return { type: "direct", url: trimmedUrl };
    }

    // For other URLs, don't assume they're embeddable - return null
    // This prevents showing the video player for non-video URLs
    return null;
  };

  const openLightbox = (index) => {
    setCurrentImageIndex(index);
    setSelectedImage(bug.screenshots[index]);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const navigateImage = (direction) => {
    const newIndex =
      (currentImageIndex + direction + bug.screenshots.length) %
      bug.screenshots.length;
    setCurrentImageIndex(newIndex);
    setSelectedImage(bug.screenshots[newIndex]);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#4DB43E]" />
        <span className="ml-3 text-gray-600">Loading bug details...</span>
      </div>
    );
  }

  if (error || !bug) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center">
        <div className="bg-white border border-gray-200 rounded-xl p-8 text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-700 font-medium">{error || "Bug not found"}</p>
          <Link to="/bugs">
            <Button className="mt-4 bg-[#4DB43E] hover:bg-[#3da030] text-white rounded-full">
              Back to Bug List
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const videoData = getPlayableVideoUrl(bug.videoUrl);

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      {/* Header */}
      <header className="border-b border-gray-200 bg-white sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/bugs" className="flex items-center gap-2">
            <img
              src="https://www.shoperator.ai/assets/shoperator_logo-BmLJu6La.svg"
              alt="Shoperator"
              className="h-8"
              data-testid="logo"
            />
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/bugs">
              <Button
                variant="ghost"
                className="text-gray-600 hover:text-gray-900 rounded-full"
                data-testid="back-to-list-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to List
              </Button>
            </Link>
            <span className="text-sm text-gray-500 hidden md:block">|</span>
            <span className="text-sm text-gray-600 hidden md:block">
              {username}
            </span>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div
          className="grid grid-cols-1 lg:grid-cols-3 gap-8"
          data-testid="bug-detail-container"
        >
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Bug Title & Badges */}
            <div className="animate-slide-up">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className="text-sm font-mono text-gray-500 bg-gray-100 px-2 py-1 rounded">
                  {bug.bugId || bug.id.slice(0, 8)}
                </span>
                {bug.severity && (
                  <Badge
                    className={`${getSeverityBadgeClass(bug.severity)} border`}
                    data-testid="severity-badge"
                  >
                    {bug.severity}
                  </Badge>
                )}
                {bug.status && (
                  <Badge
                    className={`${getStatusBadgeClass(bug.status)} border`}
                    data-testid="status-badge"
                  >
                    {bug.status}
                  </Badge>
                )}
              </div>
              <h1
                className="text-2xl md:text-3xl font-bold text-gray-900 text-heading"
                data-testid="bug-title"
              >
                {bug.title || "Untitled Bug"}
              </h1>
            </div>

            {/* Description */}
            {bug.description && (
              <Card className="border-gray-200 animate-slide-up">
                <CardHeader>
                  <CardTitle className="text-lg text-heading">Description</CardTitle>
                </CardHeader>
                <CardContent>
                  <p
                    className="text-gray-600 whitespace-pre-wrap"
                    data-testid="bug-description"
                  >
                    {bug.description}
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Steps to Reproduce */}
            {bug.stepsToReproduce && (
              <Card className="border-gray-200 animate-slide-up">
                <CardHeader>
                  <CardTitle className="text-lg text-heading">
                    Steps to Reproduce
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p
                    className="text-gray-600 whitespace-pre-wrap font-mono text-sm bg-gray-50 p-4 rounded-lg"
                    data-testid="steps-to-reproduce"
                  >
                    {bug.stepsToReproduce}
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Expected vs Actual */}
            {(bug.expectedResult || bug.actualResult) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-slide-up">
                {bug.expectedResult && (
                  <Card className="border-gray-200 border-l-4 border-l-green-500">
                    <CardHeader>
                      <CardTitle className="text-base text-heading text-green-700">
                        Expected Result
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p
                        className="text-gray-600 text-sm whitespace-pre-wrap"
                        data-testid="expected-result"
                      >
                        {bug.expectedResult}
                      </p>
                    </CardContent>
                  </Card>
                )}
                {bug.actualResult && (
                  <Card className="border-gray-200 border-l-4 border-l-red-500">
                    <CardHeader>
                      <CardTitle className="text-base text-heading text-red-700">
                        Actual Result
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p
                        className="text-gray-600 text-sm whitespace-pre-wrap"
                        data-testid="actual-result"
                      >
                        {bug.actualResult}
                      </p>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}

            {/* Video Player - Only show if there's a valid video URL */}
            {bug.videoUrl && videoData && (
              <Card className="border-gray-200 animate-slide-up overflow-hidden">
                <CardHeader>
                  <CardTitle className="text-lg text-heading flex items-center gap-2">
                    <Play className="w-5 h-5 text-[#4DB43E]" />
                    Video Recording
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="video-container" data-testid="video-player">
                    {videoData.type === "youtube" || videoData.type === "vimeo" ? (
                      <iframe
                        src={videoData.url}
                        title="Bug Video"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        className="rounded-b-xl"
                      />
                    ) : (
                      <video
                        controls
                        className="rounded-b-xl"
                        preload="metadata"
                      >
                        <source src={videoData.url} type="video/mp4" />
                        Your browser does not support the video tag.
                      </video>
                    )}
                  </div>
                  {bug.videoUrl && (
                    <div className="p-4 border-t border-gray-100">
                      <a
                        href={bug.videoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-[#4DB43E] hover:underline flex items-center gap-1"
                      >
                        <ExternalLink className="w-4 h-4" />
                        Open original video
                      </a>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Screenshots Gallery */}
            {bug.screenshots && bug.screenshots.length > 0 && (
              <Card className="border-gray-200 animate-slide-up">
                <CardHeader>
                  <CardTitle className="text-lg text-heading">
                    Screenshots ({bug.screenshots.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div
                    className="screenshot-grid"
                    data-testid="screenshots-gallery"
                  >
                    {bug.screenshots.map((screenshot, index) => (
                      <img
                        key={screenshot.id}
                        src={screenshot.url}
                        alt={screenshot.filename || `Screenshot ${index + 1}`}
                        className="screenshot-thumb"
                        onClick={() => openLightbox(index)}
                        data-testid={`screenshot-${index}`}
                      />
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Comments Section */}
            <Card className="border-gray-200 animate-slide-up">
              <CardHeader>
                <CardTitle className="text-lg text-heading flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-[#4DB43E]" />
                  Comments ({comments.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Comment List */}
                {comments.length > 0 ? (
                  <div className="space-y-4" data-testid="comments-list">
                    {comments.map((comment) => (
                      <div
                        key={comment.id}
                        className="bg-gray-50 rounded-lg p-4"
                        data-testid={`comment-${comment.id}`}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-8 h-8 bg-[#4DB43E] rounded-full flex items-center justify-center">
                            <User className="w-4 h-4 text-white" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900 text-sm">
                              {comment.author || "Anonymous"}
                            </p>
                            <p className="text-xs text-gray-500">
                              {formatDate(comment.createdTime)}
                            </p>
                          </div>
                        </div>
                        <p className="text-gray-600 text-sm whitespace-pre-wrap pl-10">
                          {comment.comment}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">
                    No comments yet. Be the first to comment!
                  </p>
                )}

                <Separator />

                {/* Add Comment Form */}
                <form
                  onSubmit={handleSubmitComment}
                  className="space-y-4"
                  data-testid="comment-form"
                >
                  <h4 className="font-medium text-gray-900">Add a Comment</h4>
                  <p className="text-sm text-gray-500">Commenting as <span className="font-medium text-gray-700">{username}</span></p>
                  <Textarea
                    placeholder="Write your comment..."
                    value={commentForm.comment}
                    onChange={(e) =>
                      setCommentForm({ ...commentForm, comment: e.target.value })
                    }
                    rows={4}
                    className="rounded-lg border-gray-300 focus:ring-[#4DB43E] focus:border-[#4DB43E]"
                    data-testid="comment-text-input"
                  />
                  <Button
                    type="submit"
                    disabled={submittingComment}
                    className="bg-[#4DB43E] hover:bg-[#3da030] text-white rounded-full"
                    data-testid="submit-comment-btn"
                  >
                    {submittingComment ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Submit Comment
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            <Card className="border-gray-200 sticky top-24 animate-slide-up">
              <CardHeader>
                <CardTitle className="text-lg text-heading">Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {bug.modulePage && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                      Module / Page
                    </p>
                    <p className="text-gray-900 font-medium" data-testid="module-page">
                      {bug.modulePage}
                    </p>
                  </div>
                )}

                {bug.environment && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                      Environment
                    </p>
                    <div className="flex items-center gap-2">
                      <Globe className="w-4 h-4 text-gray-400" />
                      <p className="text-gray-900" data-testid="environment">
                        {bug.environment}
                      </p>
                    </div>
                  </div>
                )}

                {bug.browser && bug.browser.length > 0 && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                      Browser
                    </p>
                    <div className="flex flex-wrap gap-2" data-testid="browsers">
                      {bug.browser.map((b, i) => (
                        <Badge key={i} variant="secondary" className="text-xs">
                          <Monitor className="w-3 h-3 mr-1" />
                          {b}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {bug.osDevice && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                      OS / Device
                    </p>
                    <div className="flex items-center gap-2">
                      <Smartphone className="w-4 h-4 text-gray-400" />
                      <p className="text-gray-900" data-testid="os-device">
                        {bug.osDevice}
                      </p>
                    </div>
                  </div>
                )}

                {bug.appVersion && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                      App Version
                    </p>
                    <p className="text-gray-900 font-mono text-sm" data-testid="app-version">
                      {bug.appVersion}
                    </p>
                  </div>
                )}

                <Separator />

                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                    Created
                  </p>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <p className="text-gray-900 text-sm" data-testid="created-time">
                      {formatDate(bug.createdTime)}
                    </p>
                  </div>
                </div>

                {bug.lastUpdated && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                      Last Updated
                    </p>
                    <p className="text-gray-600 text-sm" data-testid="last-updated">
                      {formatDate(bug.lastUpdated)}
                    </p>
                  </div>
                )}

                {bug.reportedBy && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                      Reported By
                    </p>
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-gray-400" />
                      <p className="text-gray-900 text-sm" data-testid="reported-by">
                        {bug.reportedBy}
                      </p>
                    </div>
                  </div>
                )}

                {bug.assignedTo && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                      Assigned To
                    </p>
                    <p className="text-gray-900 text-sm" data-testid="assigned-to">
                      {bug.assignedTo}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div
          className="lightbox-overlay"
          onClick={closeLightbox}
          data-testid="lightbox"
        >
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-white hover:text-gray-300 z-10"
            data-testid="close-lightbox-btn"
          >
            <X className="w-8 h-8" />
          </button>

          {bug.screenshots.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigateImage(-1);
                }}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 bg-black/50 rounded-full p-2"
                data-testid="prev-image-btn"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigateImage(1);
                }}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 bg-black/50 rounded-full p-2"
                data-testid="next-image-btn"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </>
          )}

          <img
            src={selectedImage.url}
            alt={selectedImage.filename}
            className="lightbox-image"
            onClick={(e) => e.stopPropagation()}
            data-testid="lightbox-image"
          />

          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-sm bg-black/50 px-4 py-2 rounded-full">
            {currentImageIndex + 1} / {bug.screenshots.length}
          </div>
        </div>
      )}
    </div>
  );
};

export default BugDetailPage;
